import AppsIcon from '@material-design-icons/svg/filled/apps.svg?react';
import ChevronRightIcon from '@material-design-icons/svg/filled/chevron_right.svg?react';
import { Link } from 'react-router';
import { TypedStatusBadge } from '../../../components/common/StatusBadge';
import { S3Image } from '../../../components/S3Image';
import {
  formatRelativeTime,
  formatVersionLabel,
} from '../../../lib/formatters';
import { getAdminDappPath } from '../../../utils/dappRoutes';
import type { SuperPublisherDetailData } from '../types';

export type PublisherDapp = SuperPublisherDetailData['dapps'][number];

export function sortDappsByActivity(dapps: PublisherDapp[]): PublisherDapp[] {
  return [...dapps].sort(
    (a, b) => (b.updated ?? b.created) - (a.updated ?? a.created),
  );
}

/** One of the publisher's apps, linking into the admin dapp page. */
export function PublisherDappRow({ dapp }: { dapp: PublisherDapp }) {
  const hasVersion =
    dapp.publishedVersionName !== null || dapp.publishedVersionCode !== null;
  const activity = dapp.updated ?? dapp.created;

  return (
    <li>
      <Link
        to={getAdminDappPath({ id: dapp.id, packageName: dapp.packageName })}
        className="-mx-2 flex items-center gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-white/[0.03]"
      >
        <S3Image
          src={dapp.dappIconUrl}
          thumbnailSrc={dapp.dappIconThumbnailUrl}
          alt=""
          aria-hidden="true"
          className="size-10 shrink-0 rounded-lg object-cover ring-1 ring-white/10"
          fallback={
            <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-white/[0.04] ring-1 ring-white/10">
              <AppsIcon className="size-5 fill-current text-neutral-500" />
            </div>
          }
        />
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <span className="truncate text-sm font-medium text-white">
              {dapp.dappName}
            </span>
            <TypedStatusBadge type="dapp" status={dapp.status} />
          </div>
          <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-2 text-xs text-neutral-500">
            <span className="truncate font-mono">
              {dapp.packageName ?? 'No package'}
            </span>
            <span aria-hidden="true" className="text-neutral-700">
              ·
            </span>
            <span className="tabular-nums">
              {hasVersion
                ? `${formatVersionLabel(dapp.publishedVersionName, dapp.publishedVersionCode)} live`
                : 'Not published'}
            </span>
            <span aria-hidden="true" className="text-neutral-700">
              ·
            </span>
            <span className="tabular-nums">
              {dapp.releaseCount}{' '}
              {dapp.releaseCount === 1 ? 'release' : 'releases'}
            </span>
          </div>
        </div>
        <span className="shrink-0 text-xs tabular-nums text-neutral-500">
          {formatRelativeTime(activity)}
        </span>
        <ChevronRightIcon
          aria-hidden="true"
          className="size-4 shrink-0 fill-neutral-600"
        />
      </Link>
    </li>
  );
}
